import { createContext, useContext, useEffect, useState, ReactNode } from 'react'

type Status = 'unknown' | 'reachable' | 'unreachable'

interface SupabaseConnectivityContextType {
  status: Status
  retry: () => void
}

const SupabaseConnectivityContext = createContext<SupabaseConnectivityContextType | undefined>(undefined)

const CHECK_TIMEOUT_MS = 10_000

/**
 * Test Auth API so sign-in can work. Supabase is used for auth only; we do not
 * require any database tables (agent catalog is static in the app).
 */
async function checkSupabaseReachable(): Promise<boolean> {
  const url = import.meta.env.VITE_SUPABASE_URL as string
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string
  if (!url || !anonKey) return false

  const authHealthPromise = fetch(`${url.replace(/\/$/, '')}/auth/v1/health`, {
    method: 'GET',
    mode: 'cors',
    headers: {
      apikey: anonKey,
      Authorization: `Bearer ${anonKey}`,
    },
  }).then((res) => res.ok)

  const timeoutPromise = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error('timeout')), CHECK_TIMEOUT_MS)
  )

  try {
    const authOk = await Promise.race([authHealthPromise, timeoutPromise])
    return authOk
  } catch {
    return false
  }
}

export function SupabaseConnectivityProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('unknown')

  const runCheck = () => {
    setStatus('unknown')
    checkSupabaseReachable().then((ok) => {
      setStatus(ok ? 'reachable' : 'unreachable')
    })
  }

  useEffect(() => {
    runCheck()
  }, [])

  return (
    <SupabaseConnectivityContext.Provider value={{ status, retry: runCheck }}>
      {children}
    </SupabaseConnectivityContext.Provider>
  )
}

export function useSupabaseConnectivity() {
  const context = useContext(SupabaseConnectivityContext)
  if (context === undefined) {
    throw new Error('useSupabaseConnectivity must be used within SupabaseConnectivityProvider')
  }
  return context
}
